/**
 * A agenda do plano, e a publicação.
 *
 * **Semana por semana, e não um calendário.** Um calendário mensal num celular
 * gasta a tela inteira para mostrar trinta quadrados vazios; o que o
 * profissional faz é percorrer os dias em ordem, escolhendo modelo ou
 * descanso. A lista por semana é essa ordem, e cabe na tela.
 *
 * **Repetir a semana é o que substitui recorrência** (plano do Marco 2, §5.7).
 * Copiar dá a liberdade de ajustar depois — a semana três raramente é igual à
 * um —, e é por isso que o P0 não tem regra de repetição.
 *
 * **Publicado não se edita — e mostra o que foi congelado.** A primeira versão
 * desenhava todo dia como um seletor de modelo, inclusive depois de publicado.
 * O seletor era alimentado pela lista **atual** de modelos, então renomear um
 * modelo mudava o nome na tela do plano publicado. O snapshot no banco estava
 * intacto o tempo todo; era a tela que mentia — e numa tela cujo propósito é
 * dizer "isto não muda mais", esse é o pior lugar possível para uma mentira.
 *
 * Agora o dia publicado lê do snapshot: título congelado e os exercícios, com a
 * prescrição de cada um. É o que o aluno vai ver, e é o que o profissional
 * precisa conferir antes de confiar.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Alert } from '@/components/Alert';
import {
  type Agenda,
  buildWeeks,
  repeatWeek,
  toAgenda,
  toWorkouts,
  type Week,
} from '@/features/plans/agenda';
import type { Plan, ScheduledWorkout } from '@/features/plans/api';
import {
  planErrorKey,
  publicationBlockers,
  useArchivePlan,
  usePlan,
  usePublishPlan,
  useSavePlan,
} from '@/features/plans/usePlans';
import { summarize } from '@/features/templates/prescriptions';
import { useTemplates } from '@/features/templates/useTemplates';
import { PlanBadge } from './StudentPlansPage';

/** Dois treinos no mesmo dia acontecem — força de manhã, corrida à tarde. */
const MAX_POR_DIA = 2;

export default function PlanEditorPage() {
  const { t } = useTranslation(['plans', 'common']);
  const { planId = '' } = useParams();
  const plano = usePlan(planId);

  if (plano.isPending) {
    return (
      <p className="px-5 py-6 text-(--color-sand-700)" aria-busy="true">
        {t('common:state.loading')}
      </p>
    );
  }

  if (plano.isError || !plano.data) {
    return (
      <div className="px-5 py-6">
        <Alert tone="error" live="assertive">
          {t(planErrorKey(plano.error))}
        </Alert>
      </div>
    );
  }

  // `key` recomeça o estado local quando o servidor devolve outra versão — sem
  // um `useEffect` sincronizando dois lugares e errando em um deles.
  return <Editor key={plano.data.version} plano={plano.data} />;
}

function Editor({ plano }: { plano: Plan }) {
  const { t } = useTranslation(['plans', 'common']);
  const gravar = useSavePlan();
  const publicar = usePublishPlan();
  const arquivar = useArchivePlan();
  const modelos = useTemplates('active');

  const [agenda, setAgenda] = useState<Agenda>(() => toAgenda(plano.workouts));

  const rascunho = plano.status === 'draft';
  // O que está na tela difere do que o servidor tem? A comparação é do que
  // seria enviado, e não do estado bruto: dois dias vazios digitados de formas
  // diferentes produzem a mesma agenda, e não são uma alteração.
  const alterado =
    JSON.stringify(toWorkouts(agenda)) !== JSON.stringify(toWorkouts(toAgenda(plano.workouts)));
  const semanas = buildWeeks(plano.start_date, plano.end_date ?? plano.start_date);
  const impedimentos = publicationBlockers(publicar.error);
  const dias = Object.values(agenda).filter((slots) => slots.length > 0).length;

  const draft = {
    title: plano.title,
    start_date: plano.start_date,
    end_date: plano.end_date,
    workouts: toWorkouts(agenda),
  };

  function trocar(date: string, indice: number, template_id: string | null) {
    const slots = [...(agenda[date] ?? [])];
    if (template_id === null) slots.splice(indice, 1);
    else slots[indice] = { template_id };
    setAgenda({ ...agenda, [date]: slots });
  }

  return (
    <div className="px-5 py-6">
      <Link
        to={`/students/${plano.relationship_id}/plans`}
        className="mh-button mh-button--ghost -ml-5"
      >
        {t('common:action.back')}
      </Link>

      <div className="mt-3 flex items-start justify-between gap-3">
        <h1 className="text-2xl">{plano.title}</h1>
        <PlanBadge status={plano.status} />
      </div>

      <p className="mt-1 text-sm text-(--color-sand-600)">
        {t('plans:daysWithWorkout', { count: dias })}
      </p>

      {!rascunho ? (
        <div className="mt-4">
          <Alert tone="info">
            {plano.status === 'published' ? t('plans:frozen') : t('plans:archived')}
          </Alert>
        </div>
      ) : null}

      {semanas.map((semana, indice) => (
        <Semana
          key={semana.number}
          semana={semana}
          agenda={agenda}
          congelados={plano.workouts}
          modelos={modelos.data?.items ?? []}
          somenteLeitura={!rascunho}
          onTrocar={trocar}
          onRepetir={
            rascunho && semanas[indice + 1]
              ? () => setAgenda(repeatWeek(agenda, semana, semanas[indice + 1]!))
              : undefined
          }
        />
      ))}

      {gravar.isError || publicar.isError || arquivar.isError ? (
        <div className="mt-5">
          <Alert tone="error" live="assertive">
            {t(planErrorKey(gravar.error ?? publicar.error ?? arquivar.error))}
          </Alert>
        </div>
      ) : null}

      {impedimentos.length > 0 ? (
        <div className="mt-3">
          <Alert tone="warning">
            <p className="font-semibold">{t('plans:blockedTitle')}</p>
            <ul className="mt-1 list-disc pl-4">
              {impedimentos.map((motivo) => (
                <li key={motivo}>{t(`plans:blocker.${motivo}`)}</li>
              ))}
            </ul>
          </Alert>
        </div>
      ) : null}

      {rascunho ? (
        <div className="mt-5 flex flex-col gap-3">
          <button
            type="button"
            disabled={gravar.isPending}
            onClick={() => gravar.mutate({ planId: plano.plan_id, draft, version: plano.version })}
            className="mh-button mh-button--secondary w-full"
          >
            {gravar.isPending ? t('common:state.loading') : t('plans:action.save')}
          </button>

          <button
            type="button"
            // **Publicar salva antes, quando há o que salvar.**
            //
            // A primeira versão exigia salvar à mão primeiro, com o argumento
            // de que juntar as duas operações esconderia qual delas falhou. O
            // argumento não se sustenta: dá para salvar, esperar, e só então
            // publicar — o erro que aparecer é o da etapa que falhou. O que a
            // separação custava era real: um toque a mais e a dúvida de quem
            // clica em publicar e não entende por que nada acontece.
            //
            // A versão vem da resposta do salvamento, e não do estado local:
            // salvar sobe a versão, e publicar com a antiga levaria 409 logo
            // depois de uma gravação bem-sucedida.
            disabled={dias === 0 || publicar.isPending || gravar.isPending}
            onClick={async () => {
              const atual = alterado
                ? await gravar.mutateAsync({
                    planId: plano.plan_id,
                    draft,
                    version: plano.version,
                  })
                : plano;
              await publicar.mutateAsync({ planId: atual.plan_id, version: atual.version });
            }}
            className="mh-button mh-button--primary w-full"
          >
            {t('plans:action.publish')}
          </button>

          <p className="text-xs text-(--color-sand-600)">{t('plans:publishHint')}</p>
        </div>
      ) : null}

      {plano.status !== 'archived' ? (
        <button
          type="button"
          disabled={arquivar.isPending}
          onClick={() => arquivar.mutate({ planId: plano.plan_id, version: plano.version })}
          className="mh-button mh-button--ghost mt-3 w-full"
        >
          {t('plans:action.archive')}
        </button>
      ) : null}
    </div>
  );
}

function Semana({
  semana,
  agenda,
  congelados,
  modelos,
  somenteLeitura,
  onTrocar,
  onRepetir,
}: {
  semana: Week;
  agenda: Agenda;
  congelados: ScheduledWorkout[];
  modelos: Array<{ template_id: string; title: string }>;
  somenteLeitura: boolean;
  onTrocar: (date: string, indice: number, template_id: string | null) => void;
  onRepetir?: () => void;
}) {
  const { t, i18n } = useTranslation(['plans']);
  const dia = new Intl.DateTimeFormat(i18n.language, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

  return (
    <section className="mt-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">{t('plans:week', { number: semana.number })}</h2>
        {onRepetir ? (
          <button type="button" onClick={onRepetir} className="mh-button mh-button--ghost text-sm">
            {t('plans:action.repeatWeek')}
          </button>
        ) : null}
      </div>

      <ul className="mt-2 flex flex-col gap-2">
        {semana.days.map((day) => {
          const slots = agenda[day.date] ?? [];
          return (
            <li
              key={day.date}
              className="rounded-xl border border-(--color-sand-200) bg-(--color-sand-0) p-3"
            >
              <span className="block text-sm font-medium text-(--color-sand-800)">
                {dia.format(new Date(`${day.date}T00:00:00Z`))}
              </span>

              {somenteLeitura ? (
                <Congelado
                  treinos={congelados.filter((treino) => treino.scheduled_for === day.date)}
                />
              ) : null}

              {!somenteLeitura &&
                slots.map((slot, indice) => (
                  <label key={indice} className="mt-2 block">
                    <span className="sr-only">
                      {t('plans:field.workoutOn', {
                        date: dia.format(new Date(`${day.date}T00:00:00Z`)),
                      })}
                    </span>
                    <select
                      value={slot.template_id ?? ''}
                      onChange={(event) => onTrocar(day.date, indice, event.target.value || null)}
                      className="w-full rounded-lg border border-(--color-sand-200) bg-(--color-sand-0) px-3 py-2"
                    >
                      <option value="">{t('plans:rest')}</option>
                      {modelos.map((modelo) => (
                        <option key={modelo.template_id} value={modelo.template_id}>
                          {modelo.title}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}

              {!somenteLeitura && slots.length < MAX_POR_DIA ? (
                <button
                  type="button"
                  onClick={() => onTrocar(day.date, slots.length, modelos[0]?.template_id ?? null)}
                  disabled={modelos.length === 0}
                  className="mh-button mh-button--ghost mt-2 text-sm"
                >
                  {slots.length === 0 ? t('plans:action.addWorkout') : t('plans:action.addSecond')}
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * O que foi congelado naquele dia — título e exercícios.
 *
 * Lê do `prescription_snapshot`, e **não** da lista de modelos. É a diferença
 * que o defeito revelou: o seletor mostrava o nome atual do modelo, então
 * renomeá-lo mudava o nome na tela de um plano publicado.
 *
 * Os exercícios aparecem porque é o que dá para conferir. Sem eles, "publicado"
 * é uma palavra na tela e o profissional não tem como saber o que o aluno
 * recebeu.
 */
function Congelado({ treinos }: { treinos: ScheduledWorkout[] }) {
  const { t } = useTranslation(['plans', 'templates']);

  if (treinos.length === 0) {
    return <span className="mt-1 block text-sm text-(--color-sand-600)">{t('plans:rest')}</span>;
  }

  return (
    <div className="mt-2 flex flex-col gap-3">
      {treinos.map((treino) => {
        const congelado = treino.prescription_snapshot;
        if (!congelado) {
          // Rascunho não chega aqui, e plano publicado sem snapshot é recusado
          // pelo banco. Se aparecer, é melhor dizer do que desenhar vazio.
          return (
            <p key={treino.workout_id} className="text-sm text-(--color-sand-600)">
              {t('plans:missingSnapshot')}
            </p>
          );
        }

        return (
          <div key={treino.workout_id}>
            <span className="block font-medium text-(--color-sand-900)">{congelado.title}</span>
            <ul className="mt-1 flex flex-col gap-1">
              {congelado.items.map((item) => (
                <li key={item.position} className="text-sm text-(--color-sand-700)">
                  {item.title}
                  <span className="ml-2 text-xs text-(--color-sand-600)">
                    {summarize(item.prescription, t)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
